import * as React from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { Animated, Easing, I18nManager, Platform, StyleProp, StyleSheet, ViewStyle } from 'react-native';

import { ds } from '@/components/lib/design.system';

type Route = {
    key: string;
};

export type Props<T extends Route> = {
    navigationState: {
        index: number;
        routes: T[];
    };
    getTabWidth: (index: number) => number;
    layout: { width: number };
    width: number | 'auto';
    gap?: number;
    style?: StyleProp<ViewStyle>;
    duration?: number;
};

const useNativeDriver = Platform.OS !== 'web';

/**
 * Custom tab bar indicator built for `react-native-tab-view`.
 *
 * Based on the default 'TabBarIndicator' implementation:
 * https://github.com/satya164/react-native-tab-view/blob/main/src/TabBarIndicator.tsx
 *
 * Unlike the default TabBarIndicator, this component allows explicit control over the animation
 * duration and easing of the indicator movement and width changes.
 */

export const CustomTabBarIndicator = <T extends Route>({
    navigationState,
    getTabWidth,
    layout,
    width,
    gap = 0,
    style,
    duration = 150,
}: Props<T>) => {
    const { index } = navigationState;
    const direction = I18nManager.isRTL ? 'rtl' : 'ltr';

    const translateX = useRef(new Animated.Value(0)).current;
    const scaleX = useRef(new Animated.Value(1)).current;

    const computeTranslateX = useCallback(
        (tabIndex: number) => {
            let offset = 0;

            for (let prevIndex = 0; prevIndex < tabIndex; prevIndex++) {
                offset += getTabWidth(prevIndex) + gap;
            }

            if (typeof width === 'number') {
                offset += getTabWidth(tabIndex) / 2 - width / 2;
            }

            return direction === 'rtl' ? -offset : offset;
        },
        [gap, getTabWidth, width, direction]
    );

    useEffect(() => {
        const toX = computeTranslateX(index);
        const toScale = width === 'auto' ? getTabWidth(index) : 1;

        Animated.parallel([
            Animated.timing(translateX, {
                toValue: toX,
                duration,
                easing: Easing.in(Easing.linear),
                useNativeDriver,
            }),
            width === 'auto'
                ? Animated.timing(scaleX, {
                      toValue: toScale,
                      duration,
                      easing: Easing.in(Easing.linear),
                      useNativeDriver,
                  })
                : Animated.delay(0),
        ]).start();
    }, [index, computeTranslateX, duration, getTabWidth, scaleX, translateX, width]);

    if (!layout.width) {
        return null;
    }

    const transform =
        width === 'auto'
            ? [{ translateX }, { scaleX }, { translateX: direction === 'rtl' ? -0.5 : 0.5 }]
            : [{ translateX }];

    const indicatorWidth = width === 'auto' ? 1 : width;

    return (
        <Animated.View
            style={[
                styles.indicator,
                {
                    width: indicatorWidth,
                    transform,
                },
                style,
            ]}
        />
    );
};

const styles = StyleSheet.create({
    indicator: {
        position: 'absolute',
        bottom: 0,
        backgroundColor: ds.colorValue.background.neutral.inverse,
    },
});
